import { useMemo, useState } from 'react';
import { BookMarked, Plus, Search } from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { MenuConfigApi } from '../../hooks/useMenuConfig';
import IconGlyph from './IconGlyph';

/**
 * Menu library — always-visible panel listing features not yet in the
 * sidebar, with search and one-click add.
 */
export default function MenuLibraryPanel({ api }: { api: MenuConfigApi }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return api.availableTopLevel;
    return api.availableTopLevel.filter(
      (f) => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q),
    );
  }, [api.availableTopLevel, query]);

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
      >
        <BookMarked size={14} style={{ color: SOVEREIGN.champagne }} />
        <div className="min-w-0 flex-1">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{ color: SOVEREIGN.ink }}
          >
            Menu library
          </p>
          <p className="text-[10.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
            Add features to the sidebar
          </p>
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums"
          style={{ background: 'rgba(201,169,97,0.14)', color: '#8A5A08' }}
        >
          {api.availableTopLevel.length}
        </span>
      </div>

      <div className="p-3">
        <div className="relative mb-2">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2"
            size={13}
            style={{ color: SOVEREIGN.kpiCaption }}
          />
          <input
            type="text"
            placeholder="Search features…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg py-1.5 pl-8 pr-3 text-[12px] focus:outline-none"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.ink,
            }}
          />
        </div>

        <div className="max-h-[32vh] space-y-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="py-4 text-center text-[11.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
              {api.availableTopLevel.length === 0
                ? 'Every available feature is already in the sidebar.'
                : 'No features match your search.'}
            </p>
          ) : (
            filtered.map((feature) => (
              <button
                key={feature.id}
                type="button"
                onClick={() => api.addMenu(feature)}
                className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors"
                style={{ border: `1px solid ${SOVEREIGN.barTrackBorder}`, background: SOVEREIGN.inputBg }}
                title={`Add ${feature.name} to the sidebar`}
              >
                <IconGlyph icon={feature.icon} className="w-4 h-4" color={SOVEREIGN.bottle} />
                <span className="min-w-0 flex-1">
                  <span
                    className="block truncate text-[12px] font-semibold"
                    style={{ color: SOVEREIGN.ink }}
                  >
                    {feature.name}
                  </span>
                  <span className="block truncate text-[10px]" style={{ color: SOVEREIGN.kpiCaption }}>
                    {feature.path}
                  </span>
                </span>
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
                  style={{
                    background: 'rgba(51,146,116,0.10)',
                    color: SOVEREIGN.bottle,
                    border: '1px solid rgba(51,146,116,0.25)',
                  }}
                >
                  <Plus size={13} />
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
